/**
 * CenaPanel — the price of the rental in the reservation form (P5-f1). It shows either the price FROZEN on the reservation (nothing in the
 * form has changed since it was saved) or the server's quote for what the form says now ("Cena po uložení"); the numbers are always the
 * server's, nothing is computed here. The two inputs the price depends on besides the term — abroad and a manual discount — live here.
 * A discount below the car minimum is allowed and warned about, never refused.
 */

import { Badge } from '@/components/Badge';
import { Checkbox } from '@/components/Checkbox';
import { Field } from '@/components/Field';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { formatInt } from '@/lib/format';
import type { CenaRozpis, RucniSleva } from './api';
import { cenaRadky, KAUCE_MAX, SKUPINA_TEXT, SLEVA_JEDNOTKY, slevaHint, type SlevaForm } from './ceny-helpers';

interface CenaPanelProps {
  /** The price to show; null while it is loading or when there is none (`duvod`). */
  rozpis: CenaRozpis | null;
  /** True: the price saved with the reservation; false: a quote for the form as it is now. */
  ulozena: boolean;
  nacita: boolean;
  /** The quote request failed. */
  chyba: boolean;
  /** Why there is no price (the server's words). */
  duvod: string | null;
  /** A note under the heading (a booking that has no agreed price yet). */
  upozorneni?: string | null;
  zahranici: boolean;
  onZahranici: (v: boolean) => void;
  sleva: SlevaForm;
  onSleva: (v: SlevaForm) => void;
  /** The deposit box as typed: empty = the car's own. */
  kauce: string;
  onKauce: (v: string) => void;
  /** The car's own deposit (shown as the default); null = the car has none set. */
  kauceVozu: number | null;
  /** Why the box is not a deposit, or null. */
  kauceChyba: string | null;
  disabled: boolean;
}

export function CenaPanel({ rozpis, ulozena, nacita, chyba, duvod, upozorneni, zahranici, onZahranici, sleva, onSleva, kauce, onKauce, kauceVozu, kauceChyba, disabled }: CenaPanelProps) {
  return (
    <section data-testid="rezervace-cena" className="flex flex-col gap-4 border border-line p-4">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">{ulozena ? 'Cena rezervace' : 'Cena po uložení'}</div>

      {upozorneni && (
        <p data-testid="rezervace-cena-upozorneni" className="text-[12px] text-ink-3">
          {upozorneni}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Checkbox
          label="Jede do zahraničí (příplatek)"
          checked={zahranici}
          disabled={disabled}
          data-testid="rezervace-zahranici"
          onChange={(e) => onZahranici(e.currentTarget.checked)}
        />
        <div className="grid grid-cols-[minmax(0,1fr)_8rem] gap-3">
          <Field label="Sleva" optional="volitelné" hint={slevaHint(sleva) ?? undefined} hintTone={slevaHint(sleva) ? 'error' : 'neutral'}>
            <TextInput
              inputMode="decimal"
              value={sleva.hodnota}
              disabled={disabled}
              data-testid="rezervace-sleva"
              onChange={(e) => onSleva({ ...sleva, hodnota: e.currentTarget.value })}
            />
          </Field>
          <Field label="Jednotka">
            <SelectInput
              value={sleva.jednotka}
              disabled={disabled}
              data-testid="rezervace-sleva-jednotka"
              onChange={(e) => onSleva({ ...sleva, jednotka: e.currentTarget.value as RucniSleva['jednotka'] })}
            >
              {SLEVA_JEDNOTKY.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </SelectInput>
          </Field>
        </div>
        <Field
          label="Kauce"
          optional="volitelné"
          hint={kauceChyba ?? `Prázdné = kauce vozu (${formatInt(kauceVozu ?? 0)} Kč). 0 = bez kauce; nejvýše ${formatInt(KAUCE_MAX)} Kč.`}
          hintTone={kauceChyba ? 'error' : 'neutral'}
        >
          <TextInput
            inputMode="numeric"
            value={kauce}
            placeholder={String(kauceVozu ?? 0)}
            disabled={disabled}
            data-testid="rezervace-kauce"
            onChange={(e) => onKauce(e.currentTarget.value)}
          />
        </Field>
      </div>

      {chyba ? (
        <p data-testid="rezervace-cena-chyba" role="alert" className="text-[13px] text-red-text">
          Cenu se nepodařilo spočítat. Zkontrolujte vstupy nebo to zkuste znovu za chvíli; rezervaci lze přesto uložit, cena se spočítá při uložení.
        </p>
      ) : nacita && !rozpis ? (
        <Skeleton className="h-24 w-full" />
      ) : rozpis ? (
        <div className="flex flex-col gap-2">
          <ul data-testid="rezervace-cena-radky" className="flex flex-col">
            {cenaRadky(rozpis).map((r) => (
              <li key={r.label} className={`flex items-baseline justify-between gap-4 border-b border-line py-1.5 text-[13px] last:border-b-0 ${r.silne ? 'font-medium text-ink' : 'text-ink-2'}`}>
                <span>{r.label}</span>
                <span className="font-mono">{r.hodnota}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-3">
            <Badge tone="neutral" size="sm">
              Skupina {rozpis.skupina}
            </Badge>
            <span>{SKUPINA_TEXT[rozpis.skupina]}</span>
          </div>
          {rozpis.pod_minimem && (
            <p data-testid="rezervace-cena-pod-minimem" role="status" className="border border-line bg-warn-soft px-3 py-2 text-[12px] text-warn">
              Sleva snižuje pronájem pod minimum vozu ({formatInt(rozpis.minimalni_najem)} Kč za {rozpis.dny} {rozpis.dny === 1 ? 'den' : rozpis.dny < 5 ? 'dny' : 'dní'}). Rezervaci lze uložit, jen o tom vězte.
            </p>
          )}
        </div>
      ) : (
        <p data-testid="rezervace-cena-nic" className="text-[13px] text-ink-2">
          {duvod ?? 'Zadejte vozidlo a termín.'}
        </p>
      )}
    </section>
  );
}
